import { apiClient } from './client'
import type { ApiResponse } from '@/types/api'

export type TextbookRequestStatus =
  | 'REQUESTED_TO_HQ'
  | 'OUTBOUND_REQUESTING'
  | 'OUTBOUND_REQUESTED'
  | 'OUTBOUND_UNCERTAIN'
  | 'SHIPPED'
  | 'RECEIVED'
  | 'CANCELLED'
  | 'REJECTED'

export interface AdminTextbookShippingView {
  receiver: string | null
  receiverTel: string | null
  receiverAddr: string | null
  missingFields: string[]
}

export interface AdminTextbookRequestView {
  id: number
  organizationId: number
  organizationName: string | null
  organizationSubdomain: string | null
  organizationOperationMode: 'PRODUCTION' | 'TEST' | null
  learnerUserId: number
  learnerName: string | null
  learnerEmail: string | null
  printBookId: number
  bookTitle: string | null
  volumeLabel: string | null
  materialType: string | null
  sourceBookId: number | null
  sourceBookTitle: string | null
  triggerLessonId: number | null
  triggerLessonTitle: string | null
  status: TextbookRequestStatus
  inGracePeriod: boolean
  requestedBy: number
  requestedByName: string | null
  requestedAt: string
  statusChangedBy: number | null
  statusChangedByName: string | null
  statusChangedAt: string | null
  memo: string | null
  statusMessage: string | null
  happydayEnabled: boolean
  happydayItemCode: number | null
  happydayIsbnCode: string | null
  shipping: AdminTextbookShippingView
  outboundOutDate: string | null
  outboundReceiverName: string | null
  outboundReceiverAddress: string | null
  outboundReceiverTel: string | null
  outboundRequestedBy: number | null
  outboundRequestedAt: string | null
  happydayAttemptCount: number
  happydayHttpStatus: number | null
  happydayResponseMessage: string | null
}

export interface AdminTextbookRequestStatusUpdateBody {
  status: 'REQUESTED_TO_HQ' | 'OUTBOUND_REQUESTED' | 'SHIPPED' | 'CANCELLED' | 'REJECTED'
  expectedStatus: TextbookRequestStatus
  attemptId: string
  reason: string | null
  memo: string | null
  statusMessage: string | null
  externalConfirmed: boolean
  externalReference: string | null
}

export interface AdminTextbookRequestBatchRejectBody {
  requestIds: number[]
  attemptId: string
  reason: string
}

export interface HappydayTextbookDispatchBody {
  attemptId: string
  outDate: string
  addressId: number
  qty: number
  supplyRate: number
}

export interface HappydayTextbookBatchDispatchBody {
  attemptId: string
  requestIds: number[]
  outDate: string
  addressId: number
  supplyRate: number
}

export interface TextbookShippingAddress {
  id: number
  label: string
  recipientName: string
  recipientPhone: string
  postalCode: string
  addressLine1: string
  addressLine2: string | null
  deliveryMemo: string | null
  isDefault: boolean
}

export interface TextbookShippingAddressInput {
  label: string
  recipientName: string
  recipientPhone: string
  postalCode: string
  addressLine1: string
  addressLine2: string | null
  deliveryMemo: string | null
  isDefault: boolean
}

export interface AdminTextbookStatusEventView {
  id: number
  fromStatus: TextbookRequestStatus | null
  toStatus: TextbookRequestStatus
  action: 'CREATED' | 'STATUS_CHANGE' | 'HAPPYDAY_DISPATCH' | 'HAPPYDAY_RESULT' | 'CORRECTION'
  source: 'LC' | 'SUPERADMIN' | 'HAPPYDAY' | 'SYSTEM'
  actorUserId: number | null
  actorName: string | null
  reason: string | null
  statusMessage: string | null
  externalConfirmed: boolean
  externalReference: string | null
  correctionOfEventId: number | null
  createdAt: string
}

export interface AdminTextbookStatusHistoryView {
  requestId: number
  currentStatus: TextbookRequestStatus
  events: AdminTextbookStatusEventView[]
  correctionTargetStatus: TextbookRequestStatus | null
  legacyTargetStatuses: TextbookRequestStatus[]
  requiresExternalConfirmation: boolean
}

export interface AdminTextbookStatusCorrectionBody {
  expectedStatus: TextbookRequestStatus
  attemptId: string
  reason: string
  externalConfirmed: boolean
  externalReference: string | null
  legacyTargetStatus: TextbookRequestStatus | null
}

export const textbookRequestsApi = {
  list: async (status?: string): Promise<AdminTextbookRequestView[]> => {
    const { data } = await apiClient.get<ApiResponse<AdminTextbookRequestView[]>>('/admin/textbook-requests', {
      params: status ? { status } : undefined,
    })
    return data.data ?? []
  },

  updateStatus: async (id: number, body: AdminTextbookRequestStatusUpdateBody): Promise<AdminTextbookRequestView> => {
    const { data } = await apiClient.patch<ApiResponse<AdminTextbookRequestView>>(
      `/admin/textbook-requests/${id}`,
      body,
    )
    return data.data!
  },

  statusHistory: async (id: number): Promise<AdminTextbookStatusHistoryView> => {
    const { data } = await apiClient.get<ApiResponse<AdminTextbookStatusHistoryView>>(
      `/admin/textbook-requests/${id}/status-history`,
    )
    return data.data!
  },

  correctLastStatus: async (
    id: number,
    body: AdminTextbookStatusCorrectionBody,
  ): Promise<AdminTextbookRequestView> => {
    const { data } = await apiClient.post<ApiResponse<AdminTextbookRequestView>>(
      `/admin/textbook-requests/${id}/status-corrections`,
      body,
    )
    return data.data!
  },

  rejectTestCenterRequests: async (
    body: AdminTextbookRequestBatchRejectBody,
  ): Promise<AdminTextbookRequestView[]> => {
    const { data } = await apiClient.post<ApiResponse<AdminTextbookRequestView[]>>(
      '/admin/textbook-requests/test-centers/reject',
      body,
    )
    return data.data ?? []
  },

  listShippingAddresses: async (id: number): Promise<TextbookShippingAddress[]> => {
    const { data } = await apiClient.get<ApiResponse<TextbookShippingAddress[]>>(
      `/admin/textbook-requests/${id}/shipping-addresses`,
    )
    return data.data ?? []
  },

  createShippingAddress: async (
    id: number,
    body: TextbookShippingAddressInput,
  ): Promise<TextbookShippingAddress> => {
    const { data } = await apiClient.post<ApiResponse<TextbookShippingAddress>>(
      `/admin/textbook-requests/${id}/shipping-addresses`,
      body,
    )
    return data.data!
  },

  dispatchToHappyday: async (
    id: number,
    body: HappydayTextbookDispatchBody,
  ): Promise<AdminTextbookRequestView> => {
    const { data } = await apiClient.post<ApiResponse<AdminTextbookRequestView>>(
      `/admin/textbook-requests/${id}/happyday-dispatch`,
      body,
    )
    return data.data!
  },

  dispatchCenterToHappyday: async (
    organizationId: number,
    body: HappydayTextbookBatchDispatchBody,
  ): Promise<AdminTextbookRequestView[]> => {
    const { data } = await apiClient.post<ApiResponse<AdminTextbookRequestView[]>>(
      `/admin/textbook-requests/organizations/${organizationId}/happyday-dispatch`,
      body,
    )
    return data.data ?? []
  },
}
